import { useState, useEffect } from "react";
import { auth } from "../../firebase";
import { sendSupportRequest } from "../../services/flightBookingAPI";
import { toast, ToastContainer } from "react-toastify";

function ContactSupportModal({ isOpen, onClose, bookingId, prefillData = {} }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: ""
  });

  // Prefill data when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        firstName: prefillData.firstName || "",
        lastName: prefillData.lastName || "",
        email: prefillData.email || "",
        phone: prefillData.phone || "",
        message: prefillData.message || ""
      });
    }
  }, [isOpen, prefillData]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const firstName = (formData.firstName || "").trim();
    const lastName = (formData.lastName || "").trim();
    const email = (formData.email || "").trim();
    const phone = (formData.phone || "").trim();
    const message = (formData.message || "").trim();

    if (!firstName || !lastName || !email || !phone || !message) {
      toast.error("All fields are mandatory");
      return;
    }

    setIsSubmitting(true);
    try {
      const user = auth.currentUser;
      const userIdentifier = (user?.email || user?.phoneNumber?.replace("+91", "") || "anon").trim();

      const payload = {
        name: `${firstName} ${lastName}`,
        email: email,
        phone: phone.startsWith("+") ? phone : `+91${phone}`,
        message: message,
        rc_booking_id: bookingId,
        From: `webbot_new:${userIdentifier}`
      };

      await sendSupportRequest(payload);
      toast.success("Support request sent successfully!");
      setFormData(prev => ({ ...prev, message: "" }));
      setTimeout(() => onClose(), 1500); // Small delay before closing
    } catch (err) {
      console.error("Support request failed:", err);
      toast.error("Failed to send support request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[8px]"
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="bg-white rounded-[24px] w-full max-w-[700px] relative z-20 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[95vh]">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-full hover:bg-slate-50 z-30"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="px-6 md:px-12 py-8 overflow-y-auto custom-scrollbar">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[36px] font-bold text-[#101828] mb-2 tracking-tight">Customer query</h2>
            <p className="text-[#475467] text-sm md:text-lg">
              We'd love to hear from you. Please fill out this form.
            </p>
          </div>

          {/* Form */}
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* First name and Last name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-8">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#344054]">
                  First name
                </label>
                <input
                  type="text"
                  placeholder="First name"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-[#D0D5DD] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] focus:ring-4 focus:ring-slate-100 focus:border-slate-500 outline-none transition-all placeholder:text-[#667085] text-[#101828] font-medium"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#344054]">
                  Last name
                </label>
                <input
                  type="text"
                  placeholder="Last name"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-[#D0D5DD] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] focus:ring-4 focus:ring-slate-100 focus:border-slate-500 outline-none transition-all placeholder:text-[#667085] text-[#101828] font-medium"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#344054]">
                Email
              </label>
              <input
                type="email"
                placeholder="you@company.com"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-[#D0D5DD] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] focus:ring-4 focus:ring-slate-100 focus:border-slate-500 outline-none transition-all placeholder:text-[#667085] text-[#101828] font-medium"
              />
            </div>

            {/* Phone number */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#344054]">
                Phone number
              </label>
              <div className="flex border border-[#D0D5DD] rounded-lg shadow-[0px_1px_2px_rgba(16,24,40,0.05)] overflow-hidden focus-within:ring-4 focus-within:ring-slate-100 focus-within:border-slate-500 transition-all">
                <div className="flex items-center gap-1.5 px-4 bg-white border-r border-[#D0D5DD] cursor-pointer hover:bg-slate-50 transition-colors">
                  <span className="text-base font-medium text-[#101828]">IN</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#667085]">
                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <input
                  type="tel"
                  placeholder="+91 00000 00000"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 outline-none placeholder:text-[#667085] text-[#101828] font-medium"
                />
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#344054]">
                Message
              </label>
              <textarea
                placeholder="Include a message..."
                rows="5"
                required
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-[#D0D5DD] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] focus:ring-4 focus:ring-slate-100 focus:border-slate-500 outline-none transition-all placeholder:text-[#667085] text-[#101828] font-medium resize-none text-[15px]"
              ></textarea>
            </div>

            {/* Submit button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 rounded-lg text-base font-bold transition-all shadow-sm bg-[#0F172A] text-white hover:bg-slate-800 active:scale-[0.99] flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : "Send message"}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer position="top-center" />
    </div>
  );
}

export default ContactSupportModal;
